/**
 * Mock data for the Exception Workspace — used when Runtime APIs are unavailable.
 */

export type CaseStatus =
  | 'detected'
  | 'investigating'
  | 'waiting_on_owner'
  | 'approval_needed'
  | 'action_taken'
  | 'resolved'
  | 'escalated'
  | 'closed'
  | 'written_off';

export type Severity = 'low' | 'medium' | 'high' | 'critical';
export type WorkflowType =
  | 'po_to_payment_exception'
  | 'inbox_ap_exception'
  | 'customer_order_risk'
  | 'procurement_exception'
  | 'vik_at_risk_exception';

export interface Signal {
  signal_id: string;
  source: string;
  domain: string;
  workflow_type: WorkflowType;
  severity: Severity;
  title: string;
  description: string;
  entity_type: string;
  entity_id: string;
  attributes: Record<string, any>;
  detected_at?: string;
  tenant_id?: string;
}

export interface ExceptionCase {
  case_id: string;
  signal_id: string;
  domain: string;
  workflow_type: WorkflowType;
  status: CaseStatus;
  severity: Severity;
  title: string;
  description: string;
  entity_type: string;
  entity_id: string;
  attributes: Record<string, any>;
  assigned_team_id?: string;
  assigned_workflow_id?: string;
  owner_id?: string;
  created_at: string;
  updated_at: string;
  tenant_id?: string;
}

export interface TimelineEvent {
  type: string;
  timestamp: string;
  data: Record<string, any>;
}

export interface EntityNode {
  entity_id: string;
  entity_type: string;
  display_name: string;
  attributes?: Record<string, any>;
}

export interface EntityEdge {
  source: string;
  target: string;
  relationship: string;
}

// ─── Mock Signals ────────────────────────────────────────────────────────────

export const mockSignals: Signal[] = [
  {
    signal_id: 'sig-001',
    source: 'scheduled_scan',
    domain: 'finance',
    workflow_type: 'po_to_payment_exception',
    severity: 'high',
    title: 'PO-Invoice quantity mismatch detected',
    description: 'PO-2024-001 ordered 100 units but invoice shows 120 units (20% variance)',
    entity_type: 'invoice',
    entity_id: 'INV-2024-001',
    attributes: { po_id: 'PO-2024-001', variance_percent: 20.0 },
    detected_at: '2026-05-31T08:15:00Z',
  },
  {
    signal_id: 'sig-002',
    source: 'rule_engine',
    domain: 'finance',
    workflow_type: 'inbox_ap_exception',
    severity: 'medium',
    title: 'Duplicate invoice detected in AP inbox',
    description: 'Invoice INV-9921 from Fabrikam Inc appears to be a duplicate of INV-9918',
    entity_type: 'invoice',
    entity_id: 'INV-9921',
    attributes: { duplicate_of: 'INV-9918', vendor: 'Fabrikam Inc' },
    detected_at: '2026-05-31T07:42:00Z',
  },
  {
    signal_id: 'sig-003',
    source: 'webhook',
    domain: 'supply_chain',
    workflow_type: 'customer_order_risk',
    severity: 'critical',
    title: 'Customer order at risk — shipment delayed',
    description: 'Order ORD-5567 shipment delayed 5 days; customer SLA breach imminent',
    entity_type: 'order',
    entity_id: 'ORD-5567',
    attributes: { customer: 'Northwind Traders', delay_days: 5 },
    detected_at: '2026-05-31T06:30:00Z',
  },
  {
    signal_id: 'sig-004',
    source: 'scheduled_scan',
    domain: 'sales_finance',
    workflow_type: 'vik_at_risk_exception',
    severity: 'high',
    title: 'VIK commitment at risk of expiry',
    description: 'Value-in-kind commitment VIK-2024-033 expires in 7 days with 40% undelivered',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-033',
    attributes: { percent_undelivered: 40, days_remaining: 7 },
    detected_at: '2026-05-31T05:00:00Z',
  },
];

// ─── Mock Exception Cases ────────────────────────────────────────────────────

export const mockCases: ExceptionCase[] = [
  {
    case_id: 'case-001',
    signal_id: 'sig-001',
    domain: 'finance',
    workflow_type: 'po_to_payment_exception',
    status: 'investigating',
    severity: 'high',
    title: 'PO-7734 payment hold — 12% price variance',
    description: 'Invoice INV-2024-0891 is $54,320 vs PO-7734 amount of $48,500. Goods receipt missing.',
    entity_type: 'invoice',
    entity_id: 'INV-2024-0891',
    attributes: { po_id: 'PO-7734', invoice_amount: 54320, po_amount: 48500, variance_percent: 12 },
    assigned_team_id: 'ap_operations_team',
    created_at: '2026-05-30T14:00:00Z',
    updated_at: '2026-05-31T08:30:00Z',
  },
  {
    case_id: 'case-002',
    signal_id: 'sig-002',
    domain: 'finance',
    workflow_type: 'inbox_ap_exception',
    status: 'approval_needed',
    severity: 'medium',
    title: 'Duplicate invoice INV-9921 — pending rejection',
    description: 'Fabrikam Inc submitted INV-9921 which matches INV-9918 (same amount, same PO ref). Recommend reject.',
    entity_type: 'invoice',
    entity_id: 'INV-9921',
    attributes: { vendor: 'Fabrikam Inc', amount: 8900, duplicate_of: 'INV-9918' },
    owner_id: 'ap_clerk_01',
    created_at: '2026-05-30T10:00:00Z',
    updated_at: '2026-05-31T07:50:00Z',
  },
  {
    case_id: 'case-003',
    signal_id: 'sig-003',
    domain: 'supply_chain',
    workflow_type: 'customer_order_risk',
    status: 'resolved',
    severity: 'critical',
    title: 'Order ORD-5567 SLA breach imminent',
    description: 'Northwind Traders order delayed 5 days. Contractual SLA is 3-day delivery. Penalty clause applies.',
    entity_type: 'order',
    entity_id: 'ORD-5567',
    attributes: { category: 'logistics', customer: 'Northwind Traders', delay_days: 5, sla_days: 3, penalty_rate: 0.02, order_value: 185000 },
    assigned_team_id: 'logistics_team',
    created_at: '2026-05-31T06:35:00Z',
    updated_at: '2026-05-31T12:40:00Z',
  },
  {
    case_id: 'case-004',
    signal_id: 'sig-004',
    domain: 'sales_finance',
    workflow_type: 'vik_at_risk_exception',
    status: 'resolved',
    severity: 'high',
    title: 'VIK-2024-033 commitment fulfilled after escalation',
    description: 'VIK commitment was at 60% delivery with 7 days remaining. Escalated to partner ops, fulfilled.',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-033',
    attributes: { percent_delivered: 100, resolution: 'fulfilled_after_escalation' },
    created_at: '2026-05-24T09:00:00Z',
    updated_at: '2026-05-30T16:00:00Z',
  },
];

// ─── Mock Timeline for case-001 ─────────────────────────────────────────────

export const mockTimeline: Record<string, TimelineEvent[]> = {
  'case-001': [
    { type: 'case_created', timestamp: '2026-05-30T14:00:00Z', data: { status: 'detected' } },
    { type: 'agent_trace', timestamp: '2026-05-30T14:01:00Z', data: { agent_name: 'Systems Analyst', operation: 'get_po', output_summary: 'PO-7734: $48,500, vendor Contoso Supplies, status APPROVED' } },
    { type: 'agent_trace', timestamp: '2026-05-30T14:02:00Z', data: { agent_name: 'Systems Analyst', operation: 'get_invoice', output_summary: 'INV-2024-0891: $54,320, 12% over PO amount' } },
    { type: 'agent_trace', timestamp: '2026-05-30T14:03:00Z', data: { agent_name: 'Systems Analyst', operation: 'get_goods_receipt', output_summary: 'No goods receipt found for PO-7734' } },
    { type: 'evidence_collected', timestamp: '2026-05-30T14:04:00Z', data: { title: 'Policy AP-003 match', evidence_type: 'policy_match', content: 'Variance >5% requires manager approval; missing GR blocks payment' } },
    { type: 'agent_trace', timestamp: '2026-05-30T14:05:00Z', data: { agent_name: 'Research Analyst', operation: 'rag_search', output_summary: 'Policy AP-003: variance >5% requires manager approval; missing GR blocks payment' } },
    { type: 'agent_trace', timestamp: '2026-05-30T14:06:00Z', data: { agent_name: 'Senior Financial Analyst', operation: 'reason', output_summary: 'Root cause: price variance (12%) + missing goods receipt. Recommend hold until GR posted.' } },
    { type: 'action', timestamp: '2026-05-30T14:07:00Z', data: { action_type: 'hold_payment', description: 'Hold payment pending goods receipt confirmation', status: 'proposed' } },
    { type: 'hitl_request', timestamp: '2026-05-31T08:00:00Z', data: { title: 'Approve payment hold for INV-2024-0891', status: 'pending', options: ['approve', 'reject', 'escalate'] } },
  ],
  'case-002': [
    { type: 'case_created', timestamp: '2026-05-30T10:00:00Z', data: { status: 'detected' } },
    { type: 'agent_trace', timestamp: '2026-05-30T10:01:00Z', data: { agent_name: 'Systems Analyst', operation: 'check_duplicate_invoice', output_summary: 'INV-9921 matches INV-9918 (same amount $8,900, same PO ref PO-4412)' } },
    { type: 'evidence_collected', timestamp: '2026-05-30T10:02:00Z', data: { title: 'Duplicate match evidence', evidence_type: 'api_response', content: 'Invoice numbers differ but amount, vendor, PO ref identical' } },
    { type: 'decision', timestamp: '2026-05-30T10:03:00Z', data: { action: 'reject', decision_source: 'agent', reasoning: 'Duplicate invoice per policy AP-007', owner: 'Senior Financial Analyst' } },
    { type: 'hitl_request', timestamp: '2026-05-31T07:50:00Z', data: { title: 'Confirm rejection of duplicate INV-9921', status: 'pending', options: ['approve', 'reject', 'escalate'] } },
  ],
  'case-003': [
    { type: 'case_created', timestamp: '2026-05-31T06:35:00Z', data: { status: 'detected' } },
    { type: 'agent_trace', timestamp: '2026-05-31T06:36:00Z', data: { agent_name: 'Supply Chain Planner', operation: 'plan', output_summary: 'Outbound delay on a penalty-bearing order. Plan: order + SLA, shipment status, alternate carriers, cost vs penalty, decision.' } },
    { type: 'agent_trace', timestamp: '2026-05-31T06:37:00Z', data: { agent_name: 'Logistics Systems Agent', operation: 'get_customer_order', output_summary: 'ORD-5567: Northwind Traders, $185,000. SLA 3-day delivery; 2%/day late penalty ($3,700/day).' } },
    { type: 'agent_trace', timestamp: '2026-05-31T06:38:00Z', data: { agent_name: 'Logistics Systems Agent', operation: 'get_shipment_status', output_summary: 'SHIP-8891 with FastFreight: carrier missed pickup window. Revised ETA is 5 days late.' } },
    { type: 'agent_trace', timestamp: '2026-05-31T06:40:00Z', data: { agent_name: 'Logistics Systems Agent', operation: 'get_alternate_carriers', output_summary: 'ExpressLine: 1-day transit, $3,200 premium. StandardShip: 2-day transit, $1,800 premium.' } },
    { type: 'agent_trace', timestamp: '2026-05-31T06:42:00Z', data: { agent_name: 'Cost & Risk Analyst', operation: 'compare_options', output_summary: 'Wait: 5 late days = $18,500 penalty. ExpressLine: $3,200, on time. StandardShip: $1,800 + 1 late day ($3,700) = $5,500. ExpressLine is cheapest overall.' } },
    { type: 'evidence_collected', timestamp: '2026-05-31T06:42:30Z', data: { title: 'Expedite vs penalty', evidence_type: 'analysis', content: 'ExpressLine ($3,200) avoids $18,500 in SLA penalties: net $15,300.' } },
    { type: 'decision', timestamp: '2026-05-31T06:44:00Z', data: { action: 'expedite_shipment', decision_source: 'agent', reasoning: 'Expedite cost far below penalty exposure. Under $5K, but first expedite for this account, so routed for approval.', owner: 'Decision Agent' } },
    { type: 'action', timestamp: '2026-05-31T06:45:00Z', data: { action_type: 'book_carrier', description: 'Book ExpressLine 1-day expedite for SHIP-8891 ($3,200).', status: 'executed' } },
    { type: 'hitl_request', timestamp: '2026-05-31T06:46:00Z', data: { title: 'Approve $3,200 expedite for ORD-5567', status: 'approved', options: ['approve', 'reject', 'escalate'] } },
    { type: 'decision', timestamp: '2026-05-31T07:10:00Z', data: { action: 'approve', decision_source: 'human', reasoning: 'Cost justified vs penalty exposure.', owner: 'Finance Director' } },
    { type: 'outcome', timestamp: '2026-05-31T12:40:00Z', data: { final_status: 'resolved', resolution_summary: 'Expedited via ExpressLine and delivered within SLA. No penalty. Net savings $15,300.', total_duration_hours: 6, human_touches: 1 } },
  ],
  'case-004': [
    { type: 'case_created', timestamp: '2026-05-24T09:00:00Z', data: { status: 'detected' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:02:00Z', data: { agent_name: 'FP&A Manager', operation: 'plan', output_summary: 'VIK-2024-033 flagged at-risk. Assigning investigation: pull commitment status, check contract terms, forecast drawdown, review precedent.' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:05:00Z', data: { agent_name: 'Budget Analyst', operation: 'get_vik_status', output_summary: 'VIK-2024-033: $4M allocation, $2.4M drawn (60%), 7 days remaining. Minimum Volume Threshold met ($1.8B vs $1.4B required).' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:07:00Z', data: { agent_name: 'Budget Analyst', operation: 'get_drawdown_history', output_summary: 'Monthly drawdown: Jan $400K, Feb $380K, Mar $420K, Apr $400K, May $410K, Jun $390K. Avg $400K/mo. Remaining $1.6M needs 4 months at current rate.' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:10:00Z', data: { agent_name: 'Contract Analyst', operation: 'extract_contract_terms', output_summary: 'Contract 00077754.0: No rollover allowed (Section C.4). Fixed VAS $4M/year. Advanced funding at start of program year. Forfeiture on unallocated amounts at year-end.' } },
    { type: 'evidence_collected', timestamp: '2026-05-24T09:11:00Z', data: { title: 'Contract term extraction', evidence_type: 'document', content: 'Schedule B, Section C.4: Unallocated Value-Added Services amounts are forfeited at the end of the program year. No rollover allowed.' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:14:00Z', data: { agent_name: 'Forecasting Analyst', operation: 'forecast_drawdown', output_summary: 'At current rate ($400K/mo), merchant will draw $400K in remaining 7 days — total $2.8M of $4M (70%). Forfeiture risk: $1.2M (30% of allocation). Probability of full utilization without intervention: 8%.' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:17:00Z', data: { agent_name: 'Business Partner Analyst', operation: 'precedent_search', output_summary: 'Found 3 similar cases: (1) Southwest 2023 — 55% drawn at 14 days, extended 30 days, fulfilled 95%. (2) Delta 2024 — 48% at 10 days, escalated, fulfilled 100%. (3) United 2023 — 62% at 7 days, no action, forfeited 28%.' } },
    { type: 'evidence_collected', timestamp: '2026-05-24T09:18:00Z', data: { title: 'Precedent analysis', evidence_type: 'precedent', content: '3/3 airline partners with similar drawdown patterns fulfilled after intervention (extension or escalation). 0/1 fulfilled without intervention.' } },
    { type: 'agent_trace', timestamp: '2026-05-24T09:20:00Z', data: { agent_name: 'FP&A Manager', operation: 'synthesize', output_summary: 'Risk assessment: $1.2M forfeiture exposure. Contract prohibits rollover. Precedent strongly supports escalation (100% fulfillment rate with intervention). Recommend: escalate to partner ops for expedited delivery within 30-day extension.' } },
    { type: 'action', timestamp: '2026-05-24T09:22:00Z', data: { action_type: 'escalate_to_partner_ops', description: 'Escalate to partner operations for expedited delivery. Request 30-day extension based on airline precedent (3/3 fulfilled after intervention).', status: 'executed' } },
    { type: 'hitl_request', timestamp: '2026-05-24T09:25:00Z', data: { title: 'Approve escalation and 30-day extension for VIK-2024-033', status: 'approved', options: ['approve', 'reject', 'escalate'] } },
    { type: 'decision', timestamp: '2026-05-28T14:00:00Z', data: { action: 'approve', decision_source: 'human', reasoning: 'Partner confirmed expedited delivery schedule. Precedent supports extension.', owner: 'Partner Ops Manager' } },
    { type: 'outcome', timestamp: '2026-05-30T16:00:00Z', data: { final_status: 'resolved', resolution_summary: 'VIK commitment fulfilled after partner escalation. All deliverables confirmed. $4M fully utilized.', total_duration_hours: 151, human_touches: 2 } },
  ],
};

// ─── Mock Entities per case ──────────────────────────────────────────────────

export const mockEntities: Record<string, { nodes: EntityNode[]; edges: EntityEdge[] }> = {
  'case-001': {
    nodes: [
      { entity_id: 'PO-7734', entity_type: 'purchase_order', display_name: 'PO-7734' },
      { entity_id: 'INV-2024-0891', entity_type: 'invoice', display_name: 'Invoice INV-2024-0891' },
      { entity_id: 'GR-MISSING', entity_type: 'goods_receipt', display_name: 'Goods Receipt (missing)' },
      { entity_id: 'PAY-HOLD-441', entity_type: 'payment', display_name: 'Payment Hold PAY-HOLD-441' },
      { entity_id: 'VEND-CS-001', entity_type: 'vendor', display_name: 'Contoso Supplies' },
    ],
    edges: [
      { source: 'INV-2024-0891', target: 'PO-7734', relationship: 'references_po' },
      { source: 'PO-7734', target: 'VEND-CS-001', relationship: 'issued_to' },
      { source: 'PAY-HOLD-441', target: 'INV-2024-0891', relationship: 'holds_payment_for' },
      { source: 'GR-MISSING', target: 'PO-7734', relationship: 'expected_for' },
    ],
  },
  'case-002': {
    nodes: [
      { entity_id: 'INV-9921', entity_type: 'invoice', display_name: 'Invoice INV-9921 (duplicate)' },
      { entity_id: 'INV-9918', entity_type: 'invoice', display_name: 'Invoice INV-9918 (original)' },
      { entity_id: 'PO-4412', entity_type: 'purchase_order', display_name: 'PO-4412' },
      { entity_id: 'VEND-FAB', entity_type: 'vendor', display_name: 'Fabrikam Inc' },
    ],
    edges: [
      { source: 'INV-9921', target: 'PO-4412', relationship: 'references_po' },
      { source: 'INV-9918', target: 'PO-4412', relationship: 'references_po' },
      { source: 'INV-9921', target: 'VEND-FAB', relationship: 'submitted_by' },
      { source: 'INV-9921', target: 'INV-9918', relationship: 'duplicate_of' },
    ],
  },
  'case-003': {
    nodes: [
      { entity_id: 'ORD-5567', entity_type: 'order', display_name: 'Order ORD-5567' },
      { entity_id: 'CUST-NW', entity_type: 'customer', display_name: 'Northwind Traders' },
      { entity_id: 'SHIP-8891', entity_type: 'shipment', display_name: 'Shipment SHIP-8891 (delayed)' },
      { entity_id: 'CARRIER-FF', entity_type: 'carrier', display_name: 'FastFreight (missed pickup)' },
      { entity_id: 'CARRIER-EXL', entity_type: 'carrier', display_name: 'ExpressLine (expedite)' },
    ],
    edges: [
      { source: 'ORD-5567', target: 'CUST-NW', relationship: 'ordered_by' },
      { source: 'SHIP-8891', target: 'ORD-5567', relationship: 'fulfills' },
      { source: 'SHIP-8891', target: 'CARRIER-FF', relationship: 'tendered_to' },
      { source: 'CARRIER-EXL', target: 'SHIP-8891', relationship: 'rebooked_on' },
    ],
  },
  'case-004': {
    nodes: [
      { entity_id: 'VIK-2024-033', entity_type: 'vik_commitment', display_name: 'VIK-2024-033 ($4M allocation)' },
      { entity_id: 'PARTNER-ADV', entity_type: 'partner', display_name: 'Adventure Works (merchant)' },
      { entity_id: 'CONTRACT-77754', entity_type: 'contract', display_name: 'Contract 00077754.0' },
      { entity_id: 'AE-SMITH', entity_type: 'account_exec', display_name: 'J. Smith (Account Exec)' },
      { entity_id: 'POLICY-VIK-NOROLL', entity_type: 'policy', display_name: 'No-Rollover Policy (Sec C.4)' },
      { entity_id: 'DRAWDOWN-HIST', entity_type: 'drawdown_history', display_name: 'Drawdown History ($2.4M / $4M)' },
      { entity_id: 'PRECEDENT-SW23', entity_type: 'precedent', display_name: 'Southwest 2023 (extended, fulfilled)' },
      { entity_id: 'PRECEDENT-DL24', entity_type: 'precedent', display_name: 'Delta 2024 (escalated, fulfilled)' },
      { entity_id: 'DECISION-EXTEND', entity_type: 'decision', display_name: 'Decision: 30-day extension approved' },
    ],
    edges: [
      { source: 'VIK-2024-033', target: 'PARTNER-ADV', relationship: 'committed_to' },
      { source: 'VIK-2024-033', target: 'CONTRACT-77754', relationship: 'governed_by' },
      { source: 'CONTRACT-77754', target: 'POLICY-VIK-NOROLL', relationship: 'contains_clause' },
      { source: 'PARTNER-ADV', target: 'AE-SMITH', relationship: 'managed_by' },
      { source: 'VIK-2024-033', target: 'DRAWDOWN-HIST', relationship: 'has_drawdown' },
      { source: 'DECISION-EXTEND', target: 'VIK-2024-033', relationship: 'decided_on' },
      { source: 'DECISION-EXTEND', target: 'PRECEDENT-SW23', relationship: 'informed_by' },
      { source: 'DECISION-EXTEND', target: 'PRECEDENT-DL24', relationship: 'informed_by' },
      { source: 'DECISION-EXTEND', target: 'POLICY-VIK-NOROLL', relationship: 'considered_policy' },
    ],
  },
};
